import { useCallback, useEffect, useMemo, useState } from 'react';
import { OpeningDaysControllerApi } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import type { DayKey } from '../constants/weekDayConstants';
import { sortDays } from '../services/openingDaysService';

export interface UseOpeningDaysResult {
  // The location's saved opening days, Monday first. Empty unless loaded for
  // this location.
  openingDays: DayKey[];
  isLoading: boolean;
  hasLoadError: boolean;
  // Fetches the days again after a failed load.
  retry: () => void;
  // Replaces the saved days. Rejects when the save fails, leaving them as they were.
  saveOpeningDays: (days: DayKey[]) => Promise<void>;
}

type LoadStatus = 'loading' | 'loaded' | 'failed';

interface OpeningDaysState {
  // The location this state describes. Every write checks it, so a response
  // for a location the user has since left never lands on the next one.
  locationId: string | null;
  status: LoadStatus;
  days: DayKey[];
}

const initialState = (locationId: string | null): OpeningDaysState => ({
  locationId,
  status: 'loading',
  days: []
});

// The days a location is open, sorted Monday first. A value the frontend does
// not know is dropped by the sort; it is reported, because the next save would
// otherwise delete it on the server without anyone noticing.
function toSortedDays(days: DayKey[], locationId: string): DayKey[] {
  const sorted = sortDays(days);
  if (sorted.length !== days.length) {
    console.error(`Unknown opening days for location ${locationId}:`, days);
  }
  return sorted;
}

// The days of the week a location is open. The state is tied to one location
// id and reset when it changes, so switching location never shows the previous
// one's days, or its failure, while the next ones load.
export function useOpeningDays(locationId: string | null): UseOpeningDaysResult {
  const api = useMemo(() => new OpeningDaysControllerApi(createApiConfiguration()), []);
  const [state, setState] = useState<OpeningDaysState>(() => initialState(locationId));
  const [attempt, setAttempt] = useState(0);

  // Reset during render rather than in an effect, so the first render at the
  // new location already reads as loading.
  if (state.locationId !== locationId) setState(initialState(locationId));

  useEffect(() => {
    if (!locationId) return;
    let isCurrent = true;
    const applyIfCurrent = (next: Omit<OpeningDaysState, 'locationId'>): void => {
      if (!isCurrent) return;
      setState((previous) =>
        previous.locationId === locationId ? { locationId, ...next } : previous
      );
    };
    // Two-argument `then`, so only a failed request reads as a failed load; a
    // bug in mapping the response surfaces as itself.
    api.getOpeningDays({ locationId }).then(
      (response) =>
        applyIfCurrent({
          status: 'loaded',
          days: toSortedDays(response.data.openingDays, locationId)
        }),
      (error: unknown) => {
        console.error(`Failed to load opening days for location ${locationId}:`, error);
        applyIfCurrent({ status: 'failed', days: [] });
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [api, locationId, attempt]);

  const retry = useCallback((): void => {
    setState((previous) => ({ ...previous, status: 'loading' }));
    setAttempt((previous) => previous + 1);
  }, []);

  const saveOpeningDays = useCallback(
    async (days: DayKey[]): Promise<void> => {
      if (!locationId) throw new Error('No location available');
      const response = await api.updateOpeningDays({
        locationId,
        updateOpeningDaysRequest: { openingDays: days }
      });
      const savedDays = toSortedDays(response.data.openingDays, locationId);
      setState((previous) =>
        previous.locationId === locationId
          ? { locationId, status: 'loaded', days: savedDays }
          : previous
      );
    },
    [api, locationId]
  );

  const current = state.locationId === locationId ? state : initialState(locationId);

  return {
    openingDays: current.status === 'loaded' ? current.days : [],
    isLoading: locationId !== null && current.status === 'loading',
    hasLoadError: locationId !== null && current.status === 'failed',
    retry,
    saveOpeningDays
  };
}
